import { type FC, useCallback, useEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { Logger } from '@egw/logger'
import { consumerEngagementService } from '../../services'
import './index.css'

const log = new Logger('Consumer Engagement Feed')

export interface ConsumerEngagementFeedProps {
  /**
   * The ID of the div element where content cards will be injected, use to target styles
   */
  id: string
  /**
   * The class name of the div element
   */
  className?: string
  /**
   * Enable custom target processing for content cards (adds custom click handlers)
   * When enabled, this will process content cards to add custom navigation behavior
   * based on the card's extras properties
   */
  enableCustomTargets?: boolean
}

export const ConsumerEngagementFeed: FC<ConsumerEngagementFeedProps> = ({
  id,
  className,
  enableCustomTargets = false,
}) => {
  const observerRef = useRef<MutationObserver | null>(null)
  const processedRef = useRef<boolean>(false)

  /**
   * Race condition-safe content card processing callback
   *
   * This useCallback handles the complex timing requirements for processing Braze content cards:
   * - Waits for cards to be cached by Braze
   * - Sets up MutationObserver to detect when cards appear in DOM
   * - Processes existing cards immediately if already rendered
   * - Includes retry mechanism for delayed card loading
   * - Prevents duplicate processing with processedRef flag
   *
   * Dependencies: [id, enableCustomTargets] - re-creates when container ID or processing flag changes
   */
  const processCardsWhenReady = useCallback(() => {
    if (!enableCustomTargets || processedRef.current) return

    const container = document.getElementById(id)
    if (!container) {
      log.info('Container element not found for card processing')
      return
    }

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    // Get cached cards from Braze
    const cards = consumerEngagementService.getCachedContentCards() ?? []

    log.info('Retrieved cached content cards', {
      cardsCount: cards.length,
      cardIds: cards.map((card) => card.id),
      cardTitles: cards.map((card: any) => card.title),
    })

    if (cards.length === 0) {
      log.info('No content cards available for processing - may need to wait for Braze to load')

      // Retry after a delay if no cards are available yet
      setTimeout(() => {
        const retryCards = consumerEngagementService.getCachedContentCards() ?? []
        log.info('Retry - Retrieved cached content cards', {
          cardsCount: retryCards.length,
          cardIds: retryCards.map((card) => card.id),
        })

        if (retryCards.length > 0 && !processedRef.current) {
          log.info('Cards available on retry, setting up observer again')
          // Recursively call with the updated cards
          processCardsWhenReady()
        }
      }, 2000) // Retry after 2 seconds

      return
    }

    log.info('Setting up MutationObserver to watch for content cards', { cardsCount: cards.length })

    // First, check if cards are already in the DOM before setting up observer
    const existingCards = Array.from(container.querySelectorAll('.ab-card'))
    const allBrazeElements = Array.from(container.querySelectorAll('[class*="ab-"]'))
    log.info('Checking for existing cards in DOM', {
      containerId: container.id,
      containerHTML: container.innerHTML.slice(0, 500) + (container.innerHTML.length > 500 ? '...' : ''),
      existingCardsCount: existingCards.length,
      allBrazeElementsCount: allBrazeElements.length,
      brazeElements: allBrazeElements.map((el) => ({
        tagName: el.tagName,
        classes: el.className,
        id: el.getAttribute('data-ab-card-id'),
      })),
      cardElements: existingCards.map((card) => ({
        id: card.getAttribute('data-ab-card-id'),
        classes: card.className,
      })),
    })

    if (existingCards.length > 0) {
      log.info('Cards already exist in DOM, processing immediately')
      setTimeout(() => {
        try {
          log.info('Processing existing cards', { cardsCount: cards.length })
          consumerEngagementService.processContentCardTargets(cards)
          processedRef.current = true
          log.info('Successfully processed existing content card targets')
        } catch (error) {
          log.error('Error processing existing content card targets', error)
        }
      }, 150)
      return // No need to set up observer if cards are already processed
    }

    // Set up MutationObserver to watch for card elements being added to DOM
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any of the added nodes are content cards
          const hasCards = Array.from(mutation.addedNodes).some(
            (node) => node instanceof Element && (node.querySelector('.ab-card') ?? node.classList.contains('ab-card')),
          )

          if (hasCards && !processedRef.current) {
            log.info('Content cards detected in DOM, processing custom targets', {
              cardsToProcess: cards.length,
              domCards: Array.from(container.querySelectorAll('.ab-card')).length,
            })

            // Small delay to ensure all cards are fully rendered before processing
            setTimeout(() => {
              try {
                log.info('Calling processContentCardTargets', { cardsCount: cards.length })
                consumerEngagementService.processContentCardTargets(cards)
                processedRef.current = true
                log.info('Successfully processed content card targets')

                // Stop observing once cards are processed
                if (observerRef.current) {
                  observerRef.current.disconnect()
                  observerRef.current = null
                }
              } catch (error) {
                log.error('Error processing content card targets', error)
              }
            }, 150)
          }
        }
      })
    })

    // Start observing the container for changes
    observerRef.current.observe(container, {
      childList: true,
      subtree: true,
    })

    // Cleanup timeout - stop observing after 15 seconds if no cards appear
    setTimeout(() => {
      if (observerRef.current && !processedRef.current) {
        log.info('MutationObserver timeout reached, cleaning up', {
          processedRef: processedRef.current,
          domCardsCount: Array.from(container.querySelectorAll('.ab-card')).length,
        })
        observerRef.current.disconnect()
        observerRef.current = null
      } else if (processedRef.current) {
        log.info('MutationObserver timeout reached but cards were already processed')
      }
    }, 15000)
  }, [id, enableCustomTargets])

  /**
   * Ref callback for managing Braze content cards lifecycle
   *
   * This useCallback serves as a ref callback that manages the entire content cards lifecycle:
   * - Initializes Braze content cards when element is attached to DOM
   * - Triggers custom target processing after giving Braze time to render
   * - Cleans up content cards and observers when element is detached
   * - Handles component unmount cleanup properly
   *
   * The callback pattern is essential here because:
   * - It's called automatically when the div ref changes (mount/unmount)
   * - Provides deterministic cleanup when component unmounts
   * - Avoids memory leaks from orphaned observers
   *
   * Dependencies: [processCardsWhenReady, enableCustomTargets] - re-creates when dependencies change
   */
  const setDivRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        // Element is attached - initialize content cards
        log.info('calling showContentCards from callback ref')
        consumerEngagementService.showContentCards(element)

        // Process cards after showing them if custom targets are enabled
        if (enableCustomTargets) {
          log.info('Custom targets enabled, setting up card processing')
          // Give Braze a moment to start rendering cards before setting up observer
          setTimeout(() => {
            log.info('Starting processCardsWhenReady after 500ms delay')
            processCardsWhenReady()
          }, 500)
        }
      } else {
        // Element is detached - cleanup content cards
        log.info('calling hideContentCards from callback ref')
        consumerEngagementService.hideContentCards()

        // Reset processing state and cleanup observer
        processedRef.current = false
        if (observerRef.current) {
          observerRef.current.disconnect()
          observerRef.current = null
        }
      }
    },
    [processCardsWhenReady, enableCustomTargets],
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        log.info('Component unmounting, cleaning up MutationObserver')
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [])

  return <div id={id} ref={setDivRef} className={twMerge('w-full h-fit has-[div]:py-xl', className)} />
}
